// INSTRUCTIONS - Outside of subaccounts and allocations, do not use .validate, .setTouched, or .setIsRequired, .hasError from any fields
// - All validation outside of subaccounts and allocations is handled by Yup validation schema

import { createSelector } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { DealBookingType } from '@src/types/OTC';
import {
  isFixedRateLoanDealForm,
  isFixedRateLoanDealFormDraft,
  isFloatingRateLoanDealForm,
  isFloatingRateLoanDealFormDraft,
  isForwardDealForm,
  isForwardDealFormDraft,
  isOptionDealForm,
  isOptionDealFormDraft,
} from '@src/types/OTC/otcGuards';
import type { CommissionCalculationTypeEnum, PayoffTypeEnum } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  DateField,
  Field,
  LoanQuoteTerminationTypeEnum,
  LoanSideEnum,
  NumericField,
  OptionTypeEnum,
  PricingModeEnum,
  SelectorField,
  SideEnum,
  Unit,
  createDateOnlyToday,
  getTypedKeys,
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  type DateOnly,
  type QtyInputCurrencyOptions,
  type Security,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { createManualDealSymbolGuards } from './manualDealSymbolSource';
import type {
  FixedRateLoanDealTradeForm,
  FloatingRateLoanDealTradeForm,
  ForwardDealTradeForm,
  ManualDealTouchState,
  ManualDealTradeCommonForm,
  ManualDealTradeForm,
  ManualDealTradeState,
  OptionDealTradeForm,
} from './types';
import { allocationTotalValidation } from './validation/FieldRules';

// Helper function to convert Allocation[] to field format
export function mapAllocationsToField(
  allocations: { subAccount: string; value: string }[],
  valueType: AllocationValueTypeEnum = AllocationValueTypeEnum.Percentage,
  isUserEntry = false
): { subAccountField: Field<string>; valueField: NumericField }[] {
  return allocations.map(alloc => ({
    subAccountField: new Field({
      name: 'Sub Account',
      value: alloc.subAccount ? alloc.subAccount : undefined,
      isTouched: isUserEntry,
    }),
    valueField: new NumericField({
      name: 'Allocation amount',
      unit: valueType === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal,
    }).updateValue(alloc.value || undefined, !isUserEntry),
  }));
}

// Type for notional calculation parameters
type NotionalCalculationParams = {
  notionalValue: string | null | undefined;
  notionalValueWithCurrency: { value: string; currency: string } | null | undefined;
  payoffType: PayoffTypeEnum | undefined;
};

// Helper function to handle notional amount calculation updates
export function updateNotionalAmountFromCalculation(
  state: WritableDraft<ManualDealTradeState>,
  params: NotionalCalculationParams
): void {
  const { notionalValue, notionalValueWithCurrency, payoffType } = params;

  // Handle Forward forms: only set value for Linear and Inverse payoff types
  if (isForwardDealFormDraft(state.form)) {
    const shouldSetValue = notionalValue && (payoffType === 'Linear' || payoffType === 'Inverse');

    // Update notional amount field
    state.form.notionalAmountField = state.form.notionalAmountField.updateValue(
      shouldSetValue ? notionalValue : undefined
    );

    // Update notional with currency fields
    state.form.notionalWithCurrencyField.value = state.form.notionalWithCurrencyField.value.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.value : undefined
    );
    state.form.notionalWithCurrencyField.currency = state.form.notionalWithCurrencyField.currency.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.currency : undefined
    );
    return;
  }

  // Handle Option forms: always set value if present (Options use Linear logic)
  if (isOptionDealFormDraft(state.form)) {
    const shouldSetValue = !!notionalValue;

    // Update notional amount field
    state.form.notionalAmountField = state.form.notionalAmountField.updateValue(
      shouldSetValue ? notionalValue : undefined
    );

    // Update notional with currency fields
    state.form.notionalWithCurrencyField.value = state.form.notionalWithCurrencyField.value.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.value : undefined
    );
    state.form.notionalWithCurrencyField.currency = state.form.notionalWithCurrencyField.currency.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.currency : undefined
    );
    return;
  }
}

export function getInitialCommonFormState(): ManualDealTradeCommonForm {
  const tradeDate = createDateOnlyToday();
  return {
    bookingTypeField: new Field<DealBookingType>({ value: 'forward' }),
    templateSymbolField: new SelectorField<Security>({ idProperty: 'Symbol', name: 'Template Symbol' }),
    symbolField: new SelectorField<Security>({ idProperty: 'Symbol', name: 'Symbol' }),
    marketField: new SelectorField<string>({ name: 'Market', isRequired: true }),
    marketAccountField: new SelectorField<string>({ name: 'Market Account', isRequired: true }),
    counterpartyField: new SelectorField<string>({ name: 'Counterparty', isRequired: true }),
    allocationValueTypeField: new Field<AllocationValueTypeEnum>({ value: AllocationValueTypeEnum.Percentage }),
    allocations: [
      {
        subAccountField: new Field<string>({ name: 'Sub Account' }),
        valueField: new NumericField({
          name: 'Allocation amount',
          unit: Unit.Percent,
        }).updateValue('100'),
      },
    ],
    tradeDateField: new Field<DateOnly>({
      name: 'Trade Date',
      value: tradeDate,
      isRequired: false,
    }),
    effectiveDateField: new Field<DateOnly>({
      name: 'Effective Date',
      value: tradeDate, // Default EffectiveTime to TradeTime value
      isRequired: false,
    }),
    settlementDateField: new DateField({ name: 'Settlement Date' }),
    expiryDateField: new DateField({ name: 'Expiry Date' }),
  };
}

export function getInitialForwardFormState(): ForwardDealTradeForm {
  const commonState = getInitialCommonFormState();
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'forward' }),
    sideField: new Field<SideEnum>({ value: SideEnum.Buy }),
    unitNotionalField: new NumericField({ name: 'Unit Notional' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    notionalAmountField: new NumericField({ name: 'Notional Amount' }),
    forwardPriceField: new NumericField({ name: 'Forward Price' }),
    pricingModeField: new Field<PricingModeEnum>({ name: 'Pricing Mode', value: PricingModeEnum.Absolute }),
    indexField: new SelectorField<string>({ name: 'Index' }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    commissionBpsField: new NumericField({ name: 'Commission', unit: Unit.Bps }),
    commissionCurrencyField: new SelectorField<string>({ name: 'Commission Currency' }),
    commissionCalculationTypeField: new Field<CommissionCalculationTypeEnum | ''>({
      name: 'Commission Calculation Type',
    }),
    initialMarginCurrencyField: new SelectorField<string>({ name: 'Initial Margin Currency' }),
    initialMarginPercentField: new NumericField({ name: 'Initial Margin %', unit: Unit.Percent }),
    releaseMarginPercentField: new NumericField({ name: 'Release Margin %', unit: Unit.Percent }),
    maintenanceMarginPercentField: new NumericField({ name: 'Maintenance Margin %', unit: Unit.Percent }),
    marginCallPercentField: new NumericField({ name: 'Margin Call %', unit: Unit.Percent }),
    liquidationMarginPercentField: new NumericField({ name: 'Liquidation Margin %', unit: Unit.Percent }),
    notionalWithCurrencyField: {
      value: new NumericField({ name: 'Notional Amount' }),
      currency: new SelectorField<string>({ name: 'Notional Currency' }),
    },
  };
}

export function getInitialFixedRateLoanFormState(): FixedRateLoanDealTradeForm {
  const commonState = getInitialCommonFormState();
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'fixedRateLoan' }),
    loanSideField: new Field<LoanSideEnum>({ value: LoanSideEnum.Lend }),
    unitLoanAmountField: new NumericField({ name: 'Unit Loan Amount' }),
    loanAmountField: new NumericField({ name: 'Loan Amount' }),
    rateField: new NumericField({ name: 'Rate', unit: Unit.Percent }),
    calculatedRateField: new NumericField({ name: 'Calculated Rate', unit: Unit.Percent }),
    paymentPeriodField: new SelectorField<string>({ name: 'Payment Period' }),
    rateSourceField: new SelectorField<string>({ name: 'Rate Source' }),
    loanOriginationFeeSpreadField: new NumericField({ name: 'Loan Origination Fee Spread', unit: Unit.Bps }),
    loanOriginationFeeField: new NumericField({ name: 'Loan Origination Fee' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    pricingModeField: new Field<PricingModeEnum>({ name: 'Pricing Mode', value: PricingModeEnum.Absolute }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    minimumLTVField: new NumericField({ name: 'Minimum LTV', unit: Unit.Percent }),
    marginCallLTVField: new NumericField({ name: 'Margin Call LTV', unit: Unit.Percent }),
    liquidationLTVField: new NumericField({ name: 'Liquidation LTV', unit: Unit.Percent }),
    collateralReleaseLTVField: new NumericField({ name: 'Collateral Release LTV', unit: Unit.Percent }),
    initialCollateralAmountField: new NumericField({ name: 'Initial Collateral Amount' }),
    initialCollateralPercentField: new NumericField({ name: 'Initial Collateral Percent', unit: Unit.Percent }),
  };
}

export function getInitialOptionFormState(): OptionDealTradeForm {
  const commonState = getInitialCommonFormState();
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'option' }),
    sideField: new Field<SideEnum>({ value: SideEnum.Buy }),
    strikePriceField: new NumericField({ name: 'Strike Price' }),
    optionTypeField: new Field<OptionTypeEnum>({ value: OptionTypeEnum.Call }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    notionalAmountField: new NumericField({ name: 'Notional Amount' }),
    notionalWithCurrencyField: {
      value: new NumericField({ name: 'Notional Amount' }),
      currency: new SelectorField<string>({ name: 'Notional Currency' }),
    },
    premiumField: new NumericField({ name: 'Premium' }),
    commissionBpsField: new NumericField({ name: 'Commission', unit: Unit.Bps }),
    commissionCurrencyField: new SelectorField<string>({ name: 'Commission Currency' }),
    initialMarginCurrencyField: new SelectorField<string>({ name: 'Initial Margin Currency' }),
    initialMarginPercentField: new NumericField({ name: 'Initial Margin %', unit: Unit.Percent }),
    releaseMarginPercentField: new NumericField({ name: 'Release Margin %', unit: Unit.Percent }),
    maintenanceMarginPercentField: new NumericField({ name: 'Maintenance Margin %', unit: Unit.Percent }),
    marginCallPercentField: new NumericField({ name: 'Margin Call %', unit: Unit.Percent }),
    liquidationMarginPercentField: new NumericField({ name: 'Liquidation Margin %', unit: Unit.Percent }),
  };
}

export function getInitialFloatingRateLoanFormState(): FloatingRateLoanDealTradeForm {
  const commonState = getInitialCommonFormState();
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'floatingRateLoan' }),
    loanSideField: new Field<LoanSideEnum>({ value: LoanSideEnum.Lend }),
    unitLoanAmountField: new NumericField({ name: 'Unit Loan Amount' }),
    loanAmountField: new NumericField({ name: 'Loan Amount' }),
    calculatedRateField: new NumericField({ name: 'Calculated Rate', unit: Unit.Percent }),
    paymentPeriodField: new SelectorField<string>({ name: 'Payment Period' }),
    rateSourceField: new SelectorField<string>({ name: 'Rate Source' }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    loanOriginationFeeSpreadField: new NumericField({ name: 'Loan Origination Fee Spread', unit: Unit.Bps }),
    loanOriginationFeeField: new NumericField({ name: 'Loan Origination Fee' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    minimumLTVField: new NumericField({ name: 'Minimum LTV', unit: Unit.Percent }),
    marginCallLTVField: new NumericField({ name: 'Margin Call LTV', unit: Unit.Percent }),
    liquidationLTVField: new NumericField({ name: 'Liquidation LTV', unit: Unit.Percent }),
    collateralReleaseLTVField: new NumericField({ name: 'Collateral Release LTV', unit: Unit.Percent }),
    initialCollateralAmountField: new NumericField({ name: 'Initial Collateral Amount' }),
    initialCollateralPercentField: new NumericField({ name: 'Initial Collateral Percent', unit: Unit.Percent }),
  };
}

export function getInitialFormState(bookingType: DealBookingType = 'forward'): ManualDealTradeForm {
  switch (bookingType) {
    case 'forward':
      return getInitialForwardFormState();
    case 'fixedRateLoan':
      return getInitialFixedRateLoanFormState();
    case 'option':
      return getInitialOptionFormState();
    case 'floatingRateLoan':
      return getInitialFloatingRateLoanFormState();
    default:
      return getInitialForwardFormState();
  }
}

export function getInitialTouchState(): ManualDealTouchState {
  return {
    bookingType: false,
    side: false,
    templateSymbol: false,
    symbol: false,
    market: false,
    marketAccount: false,
    comments: false,
    counterparty: false,
    quantity: false,
    tradeDate: false,
    effectiveTime: false,
    settlementTime: false,
    expiryTime: false,

    // Forward-specific fields
    quantityCurrency: false,
    unitNotional: false,
    notionalAmount: false,
    forwardPrice: false,
    index: false,
    spread: false,
    commission: false,
    commissionCurrency: false,
    initialMarginCurrency: false,
    initialMarginPercent: false,
    releaseMarginPercent: false,
    maintenanceMarginPercent: false,
    marginCallPercent: false,
    commissionCalculationType: false,
    liquidationMarginPercent: false,

    // Loan-specific fields
    loanSide: false,
    rate: false,
    rateSource: false,
    loanOriginationFeeSpread: false,
    loanOriginationFee: false,
    minimumLTV: false,
    marginCallLTV: false,
    liquidationLTV: false,
    collateralReleaseLTV: false,
    initialCollateralAmount: false,
    initialCollateralPercent: false,

    // Option-specific fields
    strikePrice: false,
    optionType: false,
    premium: false,
  };
}

export function getInitialState(): ManualDealTradeState {
  return {
    form: getInitialFormState(),
    touchState: getInitialTouchState(),
    referenceData: initialRefDataState,
    additionalDependencies: { tradableSubAccounts: [] },
    validationErrors: {}, // Initialize empty validation errors
    initialized: false,
    isLoading: false,
    isSubmitting: false,
    dealBeingModified: undefined,
    computedSettlementDate: undefined,
    computedExpiryDate: undefined,
  };
}

export function validateAllocations(state: WritableDraft<ManualDealTradeState>): void {
  const isSubAccountRequired = state.additionalDependencies.tradableSubAccounts.length > 0;

  state.form.allocations = state.form.allocations.map(pair => ({
    subAccountField: pair.subAccountField.setIsRequired(isSubAccountRequired).validate(),
    valueField: pair.valueField.validate(isSubAccountRequired ? [allocationTotalValidation] : [], state.form),
  }));
}

export function handleSymbolChange(
  state: WritableDraft<ManualDealTradeState>,
  symbol?: string,
  isModify = false
): void {
  if (!symbol) {
    // Spurious clears (e.g. async option refresh) must not wipe modify hydration
    if (state.dealBeingModified) {
      return;
    }
    state.form.symbolField = state.form.symbolField.updateValue(undefined);
    return;
  }

  const security = state.referenceData.securities?.securitiesBySymbol.get(symbol);
  if (!security) {
    return;
  }

  const symbolGuards = createManualDealSymbolGuards(isModify);

  if (isForwardDealFormDraft(state.form) && symbolGuards.isForward(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    // Only set unitNotional from security if not modifying a deal (let deal data take precedence)
    if (!isModify && isOTCForwardProductTemplate(security)) {
      const productDetails = security.ProductDetails;
      state.form.unitNotionalField = state.form.unitNotionalField.updateValue(productDetails.UnitNotionalAmount);
    }
    return;
  }

  if (
    (isFixedRateLoanDealFormDraft(state.form) || isFloatingRateLoanDealFormDraft(state.form)) &&
    (symbolGuards.isFixedRateLoan(security) || symbolGuards.isFloatingRateLoan(security))
  ) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    return;
  }

  if (isOptionDealFormDraft(state.form) && symbolGuards.isOption(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
  }
}

export function handleTemplateSymbolChange(
  state: WritableDraft<ManualDealTradeState>,
  symbol?: string,
  isModify = false
): void {
  if (!symbol) {
    // updateCommon does not pass isModify; during Modify Deal, SearchSelect can fire undefined when
    // otcProductTemplates refreshes — do not clear hydrated template/symbol.
    if (state.dealBeingModified) {
      return;
    }
    state.form.templateSymbolField = state.form.templateSymbolField.updateValue(undefined);
    // For new deals, also clear the symbol field when template is cleared
    if (!isModify) {
      state.form.symbolField = state.form.symbolField.updateValue(undefined);
    }
    return;
  }

  const security = state.referenceData.securities?.securitiesBySymbol.get(symbol);
  if (security) {
    state.form.templateSymbolField = state.form.templateSymbolField.updateValue(security);

    // For new deals, set the symbol field to the same value as template
    if (!isModify) {
      state.form.symbolField = state.form.symbolField.updateValue(security);
    }

    // Only set unitNotional from security if not modifying a deal (let deal data take precedence)
    if (isForwardDealFormDraft(state.form) && isOTCForwardProductTemplate(security) && !isModify) {
      const productDetails = security.ProductDetails;
      state.form.unitNotionalField = state.form.unitNotionalField.updateValue(productDetails.UnitNotionalAmount);
    }

    // Set RateSource from ProductDetails for floating rate loans
    if (isFloatingRateLoanDealFormDraft(state.form) && isFloatingRateLoanProductTemplate(security) && !isModify) {
      const productDetails = security.ProductDetails;
      if (productDetails?.RateSource) {
        state.form.rateSourceField = state.form.rateSourceField.updateValue(productDetails.RateSource);
      }
    }

    // Open-term loans have no maturity or settlement at trade entry — clear stale dates when switching templates.
    if (
      !isModify &&
      (isFixedRateLoanDealFormDraft(state.form) || isFloatingRateLoanDealFormDraft(state.form)) &&
      (isFixedRateLoanProductTemplate(security) || isFloatingRateLoanProductTemplate(security))
    ) {
      if (security.ProductDetails?.TermType === LoanQuoteTerminationTypeEnum.OpenTerm) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(undefined);
        state.computedExpiryDate = undefined;
        state.form.settlementDateField = state.form.settlementDateField.updateValue(undefined);
        state.computedSettlementDate = undefined;
      }
    }
  }
}

export function setTouchedValueForAllDealFields(state: WritableDraft<ManualDealTradeState>, touched: boolean): void {
  // Set all form fields as touched
  getTypedKeys(state.form).forEach(key => {
    const field = state.form[key];
    if (field && typeof field === 'object' && 'setTouched' in field) {
      field.setTouched(touched);
    }
  });
}

export const selectManualDealUseTradeAllocations = createSelector(
  (state: AppState) => state.referenceData.settings.useTradeAllocations,
  (state: AppState) => state.manualDeal.dealBeingModified,
  (useTradeAllocationsSetting, dealBeingModified) => {
    return dealBeingModified ? dealBeingModified.deal.Allocation != null : useTradeAllocationsSetting !== false;
  }
);

/**
 * Selector for the quantity currency options for a forward deal (based on BaseAsset, QuoteAsset, or Units)
 */
export const selectForwardDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isForwardDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || !symbolGuards.isForward(security)) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BaseAsset, QuoteAsset } = productDetails;
    const options: QtyInputCurrencyOptions<string>[] = [];

    if (BaseAsset) {
      options.push({ currency: BaseAsset, label: BaseAsset });
    }
    if (QuoteAsset) {
      options.push({ currency: QuoteAsset, label: QuoteAsset });
    }

    // Always include Units option for forward deals
    options.push({ currency: 'Units', label: 'Units' });

    return options;
  }
);

/**
 * Selector for the quantity currency options for a loan deal (based on BorrowedAsset or Units)
 * Applies to both Fixed Rate Loans and Floating Rate Loans
 */
export const selectLoanDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isFixedRateLoanDealForm(form) && !isFloatingRateLoanDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || (!symbolGuards.isFixedRateLoan(security) && !symbolGuards.isFloatingRateLoan(security))) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BorrowedAsset } = productDetails;
    const options: QtyInputCurrencyOptions<string>[] = [];

    if (BorrowedAsset) {
      options.push({ currency: BorrowedAsset, label: BorrowedAsset });
    }

    options.push({ currency: 'Units', label: 'Units' });

    return options;
  }
);

/**
 * Selector for the quantity currency options for an option deal (based on BaseAsset, QuoteAsset, or Units)
 */
export const selectOptionDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isOptionDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || !symbolGuards.isOption(security)) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BaseAsset, QuoteAsset } = productDetails;
    const optionsByCurrency = new Map<string, QtyInputCurrencyOptions<string>>();

    if (BaseAsset) {
      optionsByCurrency.set(BaseAsset, { currency: BaseAsset, label: BaseAsset });
    }
    if (QuoteAsset) {
      optionsByCurrency.set(QuoteAsset, { currency: QuoteAsset, label: QuoteAsset });
    }

    // Always include Units option for option deals
    optionsByCurrency.set('Units', { currency: 'Units', label: 'Units' });
    return Array.from(optionsByCurrency.values());
  }
);
